// src/components/ui/EmptyState.jsx
import React from 'react';
import { Inbox, Info, AlertTriangle, AlertOctagon } from 'lucide-react';
import './EmptyState.css';

const VARIANTS = {
  info: { Icon: Info, className: 'empty-state--info' },
  warning: { Icon: AlertTriangle, className: 'empty-state--warning' },
  error: { Icon: AlertOctagon, className: 'empty-state--error' },
  neutral: { Icon: Inbox, className: 'empty-state--neutral' },
};

/**
 * EmptyState — reusable card for "nothing here yet", "not allowed",
 * "filters returned nothing" and similar dead ends.
 *
 * Variants:
 *   - 'neutral' (default): grey, plain inbox icon — first-time empty.
 *   - 'info': violet accent, info icon — informational gating
 *     (e.g. NOT_AN_ENTERPRISE_ORG).
 *   - 'warning': amber accent, alert-triangle — recoverable issues
 *     (filters returned nothing; recommend resetting).
 *   - 'error': red accent, alert-octagon — unexpected failures.
 *
 * Optional `icon` prop overrides the variant default. Optional
 * `action` is a {label, onClick} pair for a primary CTA.
 *
 * Sized to drop into existing card grids (min-height: 180px).
 */
export default function EmptyState({
  variant = 'neutral',
  icon,
  title,
  description,
  action,
  requestId,
  children,
}) {
  const { Icon, className } = VARIANTS[variant] ?? VARIANTS.neutral;
  const RenderedIcon = icon ?? <Icon size={28} aria-hidden="true" />;

  return (
    <div className={`empty-state ${className}`} role="status">
      <div className="empty-state__icon">{RenderedIcon}</div>
      {title && <h3 className="empty-state__title">{title}</h3>}
      {description && (
        <p className="empty-state__description">{description}</p>
      )}
      {children}
      {requestId && (
        <small className="empty-state__request-id">ref: {requestId}</small>
      )}
      {action && (
        <button
          type="button"
          className="empty-state__action"
          onClick={action.onClick}
        >
          {action.label}
        </button>
      )}
    </div>
  );
}
